import * as React from "react";
import { HeaderIcon } from "../atoms/header-icon.js";
import { cx } from "../internal/cx.js";
import { type BreadcrumbCrumb, BreadcrumbTrail } from "../molecules/breadcrumb.js";
import { HeadingGroup } from "../molecules/heading-group.js";

/**
 * PageHeader — top-level page heading for app screens.
 *
 * Use once per page. Owns h1 semantics and page-level chrome such as
 * breadcrumbs, metadata, and right-aligned actions. Use SectionHeader for
 * card/section h2 headings inside the page body.
 */
export type PageHeaderAlign = "start" | "center";
/** How the leading icon renders — bare muted icon (default) or tinted medallion. */
export type PageHeaderIconTreatment = "bare" | "medallion";

export interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** Optional breadcrumb trail above the page title. */
  breadcrumbs?: BreadcrumbCrumb[];
  /** Optional small label above the title. */
  eyebrow?: React.ReactNode;
  /** Main page title. Renders as h1. */
  title: React.ReactNode;
  /** Supporting copy under the title. */
  description?: React.ReactNode;
  /** Small metadata/status slot next to the title. */
  meta?: React.ReactNode;
  /** Primary page actions, usually one or two Buttons. */
  actions?: React.ReactNode;
  /** Optional leading visual for dashboard landing pages. */
  icon?: React.ReactNode;
  /** Icon rendering — bare muted icon (default, V2 baseline) or the medallion. */
  iconTreatment?: PageHeaderIconTreatment;
  /** Vertical alignment between text and actions. */
  align?: PageHeaderAlign;
}

export const PageHeader = React.forwardRef<HTMLElement, PageHeaderProps>(function PageHeader(
  {
    actions,
    align = "start",
    breadcrumbs,
    className,
    description,
    eyebrow,
    icon,
    iconTreatment = "bare",
    meta,
    title,
    ...props
  },
  ref,
) {
  const bareIcon = iconTreatment === "bare";
  return (
    <header
      ref={ref}
      className={cx(
        "cw-v2-page-header",
        `cw-v2-page-header--align-${align}`,
        icon ? "cw-v2-page-header--has-icon" : null,
        icon && bareIcon ? "cw-v2-page-header--icon-bare" : null,
        actions ? "cw-v2-page-header--has-actions" : null,
        className,
      )}
      {...props}
    >
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <BreadcrumbTrail className="cw-v2-page-header__breadcrumbs" items={breadcrumbs} />
      ) : null}
      <div className="cw-v2-page-header__body">
        {icon ? (
          bareIcon ? (
            <span aria-hidden className="cw-v2-page-header__icon cw-v2-page-header__icon--bare">
              {icon}
            </span>
          ) : (
            <HeaderIcon className="cw-v2-page-header__icon" icon={icon} size="lg" variant="blue" />
          )
        ) : null}
        <HeadingGroup
          className="cw-v2-page-header__content"
          description={description}
          descriptionVariant="body"
          eyebrow={eyebrow}
          meta={meta}
          title={title}
          titleVariant="display"
          variant="page"
        />
        {actions ? <div className="cw-v2-page-header__actions">{actions}</div> : null}
      </div>
    </header>
  );
});
PageHeader.displayName = "PageHeader";
